import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { useAuth } from '../utils/authContext';
import { AlertCircle, Plus, Minus, Trash2, X, ChevronDown, ChevronUp, Package, User, Truck, CreditCard, FileText } from 'lucide-react';
import { GET_CATALOG_ITEMS_AND_CATEGORIES, GET_SHIPPING_OPTIONS } from '../graphql/queries';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';

// GraphQL mutation for creating manual transactions
const CREATE_MANUAL_TRANSACTION = gql`
  mutation CreateManualTransaction($input: ManualTransactionInput!) {
    createManualTransaction(input: $input) {
      id
      reference
      status
      amount
      finalAmount
      dateCreated
    }
  }
`;

const ManualTransactionForm = ({ onSuccess, onCancel, isOpen }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        status: 'SUCCESS',
        paymentMethod: 'CASH',
        transactionDate: new Date().toISOString().split('T')[0],
        notes: '',
        shippingAddress: '',
        shippingState: '',
        shippingCountry: 'Nigeria',
        shippingCost: 0,
        shippingMethod: ''
    });

    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [shippingOptions, setShippingOptions] = useState([]);
    const [selectedShippingOption, setSelectedShippingOption] = useState(null);

    // Section visibility toggles
    const [showCustomerInfo, setShowCustomerInfo] = useState(true);
    const [showShippingInfo, setShowShippingInfo] = useState(true);
    const [showTransactionDetails, setShowTransactionDetails] = useState(true);

    // Custom dropdown styles - improved to match ShippingOptions
    const dropdownStyles = "w-full bg-white px-3 py-2 block rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50";
    const dropdownArrowStyles = "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 viewBox=%220 0 20 20%22%3E%3Cpath stroke=%22%236b7280%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22 stroke-width=%222%22 d=%22M6 8l4 4 4-4%22/%3E%3C/svg%3E')] bg-[right_0.5rem_center] bg-[length:20px_20px] bg-no-repeat pr-10";

    // Fetch catalog items and categories
    const { loading: itemsLoading, error: itemsError, data: itemsData } = useQuery(GET_CATALOG_ITEMS_AND_CATEGORIES, {
        variables: { catalogId: user?.selectedCatalog?.id },
        skip: !user?.selectedCatalog?.id
    });

    // Fetch shipping options
    const { loading: shippingLoading, error: shippingError, data: shippingData } = useQuery(GET_SHIPPING_OPTIONS, {
        variables: { catalogId: user?.selectedCatalog?.id },
        skip: !user?.selectedCatalog?.id
    });

    // Create transaction mutation
    const [createTransaction, { loading }] = useMutation(CREATE_MANUAL_TRANSACTION, {
        onCompleted: (data) => {
            setSuccess(`Transaction ${data.createManualTransaction.reference} created successfully!`);
            setIsSubmitting(false);
            setTimeout(() => {
                onSuccess(data.createManualTransaction);
            }, 1500);
        },
        onError: (error) => {
            setError(`Error: ${error.message}`);
            setIsSubmitting(false);
        }
    });

    useEffect(() => {
        if (itemsData?.catalogItemsAndCategories) {
            // Combine categorized and uncategorized items into a flat array
            const categorizedItems = itemsData.catalogItemsAndCategories.categories
                .flatMap(category => category.items || []);

            const uncategorizedItems = itemsData.catalogItemsAndCategories.uncategorizedItems || [];

            // Merge all items into a single array
            setItems([...categorizedItems, ...uncategorizedItems]);
        }
    }, [itemsData]);

    // Process shipping options data
    useEffect(() => {
        if (shippingData?.getShippingOptions) {
            // Sort shipping options to put pickup options first
            const options = [...shippingData.getShippingOptions];
            options.sort((a, b) => {
                // Put pickup options first
                if (a.isPickup && !b.isPickup) return -1;
                if (!a.isPickup && b.isPickup) return 1;
                // Otherwise maintain normal ordering
                return 0;
            });
            setShippingOptions(options);
        }
    }, [shippingData]);

    // Handle shipping option selection
    const handleShippingOptionChange = (e) => {
        const optionId = e.target.value;

        if (!optionId) {
            setSelectedShippingOption(null);
            setFormData(prev => ({
                ...prev,
                shippingMethod: '',
                shippingCost: 0
            }));
            return;
        }

        const option = shippingOptions.find(opt => opt.id === optionId);

        if (option) {
            setSelectedShippingOption(option);
            setFormData(prev => ({
                ...prev,
                shippingMethod: option.name,
                shippingCost: option.price || 0,
                // For pickup options, pre-fill address with the pickup details
                shippingAddress: option.isPickup ? option.details : prev.shippingAddress
            }));
        }
    };

    // Form validation state
    const [validationErrors, setValidationErrors] = useState({});
    const [touched, setTouched] = useState({});

    // Mark field as touched when user interacts with it
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));

        // Validate the field on blur
        validateField(name, formData[name]);
    };

    // Validate a specific field
    const validateField = (fieldName, value) => {
        let errors = { ...validationErrors };

        switch (fieldName) {
            case 'customerName':
                if (!value.trim()) {
                    errors.customerName = 'Customer name is required';
                } else {
                    delete errors.customerName;
                }
                break;
            case 'customerEmail':
                if (value && !/^\S+@\S+\.\S+$/.test(value)) {
                    errors.customerEmail = 'Please enter a valid email address';
                } else {
                    delete errors.customerEmail;
                }
                break;
            case 'shippingAddress':
                if (!selectedShippingOption?.isPickup && !value.trim()) {
                    errors.shippingAddress = 'Shipping address is required for delivery';
                } else {
                    delete errors.shippingAddress;
                }
                break;
            // Add more validations as needed
            default:
                break;
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Update handleInputChange to include validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // If the field has been touched, validate as user types
        if (touched[name]) {
            validateField(name, value);
        }
    };

    // Enhanced form submission with validation
    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Touch all fields to show validation errors
        const allFields = ['customerName', 'customerEmail', 'shippingAddress', 'shippingState', 'shippingCountry'];
        const newTouched = {};
        allFields.forEach(field => { newTouched[field] = true; });
        setTouched(newTouched);

        // Validate all fields
        let valid = true;
        allFields.forEach(field => {
            if (!validateField(field, formData[field])) {
                valid = false;
            }
        });

        if (!valid) {
            setError('Please fix the highlighted errors before submitting');
            return;
        }

        if (selectedItems.length === 0) {
            setError('Please add at least one item');
            return;
        }

        if (!selectedShippingOption) {
            setError('Please select a shipping option');
            return;
        }

        const subtotal = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const shippingCost = parseFloat(formData.shippingCost) || 0;
        const finalAmount = subtotal + shippingCost;

        setIsSubmitting(true);

        // Prepare the transaction input
        const transactionInput = {
            customerName: formData.customerName,
            customerPhone: formData.customerPhone,
            customerEmail: formData.customerEmail,
            catalogId: user.selectedCatalog.id,
            amount: subtotal,
            finalAmount: finalAmount,
            status: formData.status,
            paymentMethod: formData.paymentMethod,
            transactionDate: formData.transactionDate,
            notes: formData.notes,
            items: selectedItems.map(item => ({
                itemId: item.id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                variantIndex: item.variantIndex
            })),
            shipping: {
                method: formData.shippingMethod,
                address: formData.shippingAddress,
                state: formData.shippingState,
                country: formData.shippingCountry,
                cost: shippingCost
            }
        };

        createTransaction({
            variables: { input: transactionInput }
        });
    };

    // Helper function to determine input class based on validation
    const getInputClassName = (fieldName) => {
        const baseClasses = "w-full px-2 py-2 block rounded-md shadow-sm focus:ring focus:ring-opacity-50 h-10";

        if (touched[fieldName] && validationErrors[fieldName]) {
            return `${baseClasses} border-red-300 focus:border-red-500 focus:ring-red-500`;
        }

        return `${baseClasses} border-gray-300 focus:border-[#191970] focus:ring-[#191970]`;
    };

    // Add item to the transaction
    const handleAddItem = () => {
        const itemSelect = document.getElementById('itemSelect');
        const selectedOption = itemSelect.options[itemSelect.selectedIndex];

        if (!selectedOption.value) {
            setError('Please select an item');
            return;
        }

        const selectedItem = items.find(item => item.id === selectedOption.value);
        if (!selectedItem) return;

        const variantSelect = document.getElementById('variantSelect');
        let price = 0;
        let variantIndex = undefined;
        let variantName = '';

        if (selectedItem.hasVariants && variantSelect) {
            const selectedVariantIndex = parseInt(variantSelect.value, 10);
            if (selectedVariantIndex >= 0 && selectedItem.priceVariants[selectedVariantIndex]) {
                price = selectedItem.priceVariants[selectedVariantIndex].price;
                variantIndex = selectedVariantIndex;
                variantName = selectedItem.priceVariants[selectedVariantIndex].variant;
            } else {
                setError('Please select a variant');
                return;
            }
        } else {
            price = selectedItem.priceVariants[0].price;
        }

        // Check if item is out of stock
        if (selectedItem.manageInventory) {
            if (selectedItem.hasVariants && variantIndex !== undefined) {
                const variant = selectedItem.priceVariants[variantIndex];
                if (variant.stock <= 0) {
                    setError(`${selectedItem.name} (${variantName}) is out of stock`);
                    return;
                }
            } else if (selectedItem.stockTotal <= 0) {
                setError(`${selectedItem.name} is out of stock`);
                return;
            }
        }

        // Add to selected items list
        setSelectedItems(prev => [
            ...prev,
            {
                id: selectedItem.id,
                name: selectedItem.name,
                price: price,
                quantity: 1,
                variantName: variantName,
                variantIndex: variantIndex,
                stockTotal: selectedItem.stockTotal,
                manageInventory: selectedItem.manageInventory
            }
        ]);

        itemSelect.selectedIndex = 0;
        if (variantSelect) variantSelect.selectedIndex = 0;
        setError('');
    };

    // Update item quantity
    const updateItemQuantity = (index, newQuantity) => {
        const item = selectedItems[index];

        // Check stock limits
        if (item.manageInventory) {
            const stockLimit = item.variantIndex !== undefined ?
                items.find(i => i.id === item.id)?.priceVariants[item.variantIndex]?.stock :
                item.stockTotal;

            if (stockLimit !== undefined && newQuantity > stockLimit) {
                setError(`Only ${stockLimit} units available for ${item.name}${item.variantName ? ` (${item.variantName})` : ''}`);
                return;
            }
        }

        if (newQuantity > 0) {
            const updatedItems = [...selectedItems];
            updatedItems[index] = { ...item, quantity: newQuantity };
            setSelectedItems(updatedItems);
        }
    };

    // Remove item
    const removeItem = (index) => {
        setSelectedItems(prev => prev.filter((_, i) => i !== index));
    };

    // Calculate totals
    const subtotal = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shippingCost = parseFloat(formData.shippingCost) || 0;
    const total = subtotal + shippingCost;

    // Handle item selection to show appropriate variants
    const [selectedItemId, setSelectedItemId] = useState('');
    const selectedItemData = items.find(item => item.id === selectedItemId);

    if (!isOpen) return null;

    // Handle country selection
    const selectCountry = (val) => {
        setFormData(prev => ({
            ...prev,
            shippingCountry: val,
            shippingState: '' // Reset state when country changes
        }));
    };

    // Handle state/region selection
    const selectRegion = (val) => {
        setFormData(prev => ({
            ...prev,
            shippingState: val
        }));
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold text-[#191970]">Add Manual Transaction</h2>
                        <button
                            onClick={onCancel}
                            className="text-gray-500 bg-gray-100 hover:text-gray-700 p-1.5 rounded-full"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-start">
                            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md">
                            {success}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Customer Information */}
                        <div className="bg-white rounded-md">
                            <button
                                type="button"
                                onClick={() => setShowCustomerInfo(!showCustomerInfo)}
                                className="flex w-full items-center text-[#191970] hover:text-opacity-80 py-2"
                            >
                                {showCustomerInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                <User size={18} className="ml-1 mr-2" />
                                <span className="font-medium">Customer Information</span>
                            </button>

                            {showCustomerInfo && (
                                <div className="mt-2 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Customer Name*
                                            </label>
                                            <input
                                                type="text"
                                                name="customerName"
                                                value={formData.customerName}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                className={getInputClassName('customerName')}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Phone Number <span className="text-[70%]">(optional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="customerPhone"
                                                value={formData.customerPhone}
                                                onChange={handleInputChange}
                                                className="w-full px-2 py-2 block rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Email Address <span className="text-[70%]">(optional)</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="customerEmail"
                                                value={formData.customerEmail}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                className={getInputClassName('customerEmail')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Transaction Items */}
                        <div className="bg-white rounded-md">
                            <h3 className="text-lg font-medium text-[#191970] mb-3 flex items-center">
                                <Package size={18} className="mr-2" />
                                Items
                            </h3>

                            {/* Item Selection */}
                            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-500 mb-1">
                                        Select Item
                                    </label>
                                    <select
                                        id="itemSelect"
                                        className={`${dropdownStyles} ${dropdownArrowStyles}`}
                                        onChange={(e) => setSelectedItemId(e.target.value)}
                                        value={selectedItemId}
                                        disabled={itemsLoading}
                                    >
                                        <option value="">-- Select an item --</option>
                                        {items.map(item => (
                                            <option key={item.id} value={item.id}>
                                                {item.name} {item.manageInventory ?
                                                    `(${item.hasVariants ? 'Multiple variants' : `Stock: ${item.stockTotal}`})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedItemData?.hasVariants && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500 mb-1">
                                            Select Variant
                                        </label>
                                        <select
                                            id="variantSelect"
                                            className={`${dropdownStyles} ${dropdownArrowStyles}`}
                                        >
                                            <option value="">-- Select --</option>
                                            {selectedItemData.priceVariants.map((variant, index) => (
                                                <option
                                                    key={index}
                                                    value={index}
                                                    disabled={selectedItemData.manageInventory && variant.stock <= 0}
                                                >
                                                    {variant.variant} - ₦{variant.price.toLocaleString()}
                                                    {selectedItemData.manageInventory ? ` (Stock: ${variant.stock})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className={selectedItemData?.hasVariants ? "md:col-span-3" : ""}>
                                    <button
                                        type="button"
                                        onClick={handleAddItem}
                                        className="mt-2 w-full bg-[#191970] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center"
                                    >
                                        <Plus size={16} className="mr-1" />
                                        Add Item
                                    </button>
                                </div>
                            </div>

                            {/* Selected Items */}
                            {selectedItems.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Items:</h4>
                                    <div className="border rounded-md overflow-hidden mb-4">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {selectedItems.map((item, index) => (
                                                        <tr key={index} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3">
                                                                <div className="text-sm font-medium">{item.name}</div>
                                                                {item.variantName && (
                                                                    <div className="text-xs text-gray-500">{item.variantName}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="text-sm">₦{item.price.toLocaleString()}</div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateItemQuantity(index, item.quantity - 1)}
                                                                        className="p-1 bg-gray-100 rounded-l-md border border-r-0 border-gray-300"
                                                                    >
                                                                        <Minus className="w-3 h-3" />
                                                                    </button>
                                                                    <input
                                                                        type="number"
                                                                        value={item.quantity}
                                                                        onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)}
                                                                        className="w-12 text-center border-y border-gray-300 p-1 text-sm"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateItemQuantity(index, item.quantity + 1)}
                                                                        className="p-1 bg-gray-100 rounded-r-md border border-l-0 border-gray-300"
                                                                    >
                                                                        <Plus className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right">
                                                                <div className="text-sm font-medium">₦{(item.price * item.quantity).toLocaleString()}</div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeItem(index)}
                                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-full"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Shipping Information */}
                        <div className="bg-white rounded-md">
                            <button
                                type="button"
                                onClick={() => setShowShippingInfo(!showShippingInfo)}
                                className="flex w-full items-center text-[#191970] hover:text-opacity-80 py-2"
                            >
                                {showShippingInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                <Truck size={18} className="ml-1 mr-2" />
                                <span className="font-medium">Shipping Information</span>
                            </button>

                            {showShippingInfo && (
                                <div className="mt-2 space-y-4">
                                    {/* Shipping Option Selector */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-500 mb-1">
                                            Shipping Option*
                                        </label>
                                        <select
                                            name="shippingOption"
                                            value={selectedShippingOption?.id || ''}
                                            onChange={handleShippingOptionChange}
                                            className={`${dropdownStyles} ${dropdownArrowStyles}`}
                                            disabled={shippingLoading}
                                        >
                                            <option value="">-- Select shipping option --</option>
                                            {shippingOptions.map(option => (
                                                <option key={option.id} value={option.id}>
                                                    {option.name} - {option.isPickup ? 'Pickup' : `₦${option.price.toLocaleString()}`}
                                                </option>
                                            ))}
                                        </select>

                                        {shippingOptions.length === 0 && !shippingLoading && (
                                            <p className="mt-1 text-sm text-amber-600">
                                                No shipping options configured. You can add them in the Shipping Options page.
                                            </p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Delivery Address
                                            </label>
                                            <textarea
                                                name="shippingAddress"
                                                rows={3}
                                                className={`w-full px-2 py-2 block rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 ${selectedShippingOption?.isPickup ? 'bg-gray-50' : ''}`}
                                                placeholder={selectedShippingOption?.isPickup ? "Pickup location details will be shown here" : "Enter delivery address"}
                                                value={formData.shippingAddress}
                                                onChange={handleInputChange}
                                                readOnly={selectedShippingOption?.isPickup}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center">
                                                    <span>Country</span>
                                                    {!selectedShippingOption?.isPickup && (
                                                        <span className="ml-1 text-xs text-red-500">*</span>
                                                    )}
                                                </label>
                                                <CountryDropdown
                                                    value={formData.shippingCountry}
                                                    onChange={selectCountry}
                                                    className={`${dropdownStyles} ${dropdownArrowStyles} ${selectedShippingOption?.isPickup ? 'bg-gray-50' : ''}`}
                                                    priorityOptions={['NG']}
                                                    disabled={selectedShippingOption?.isPickup}
                                                />
                                                {!formData.shippingCountry && !selectedShippingOption?.isPickup && (
                                                    <p className="mt-1 text-xs text-amber-600">Required for shipping</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center">
                                                    <span>State/Region</span>
                                                    {!selectedShippingOption?.isPickup && (
                                                        <span className="ml-1 text-xs text-red-500">*</span>
                                                    )}
                                                </label>
                                                <RegionDropdown
                                                    country={formData.shippingCountry}
                                                    value={formData.shippingState}
                                                    onChange={selectRegion}
                                                    className={`${dropdownStyles} ${dropdownArrowStyles} ${selectedShippingOption?.isPickup ? 'bg-gray-50' : formData.shippingCountry && !formData.shippingState ? 'border-amber-300 focus:border-amber-500' : ''}`}
                                                    blankOptionLabel="Select State/Region"
                                                    disabled={selectedShippingOption?.isPickup || !formData.shippingCountry}
                                                />
                                                {formData.shippingCountry && !formData.shippingState && !selectedShippingOption?.isPickup && (
                                                    <p className="mt-1 text-xs text-amber-600">Please select a state/region</p>
                                                )}
                                            </div>

                                            {/* Add helpful user guidance */}
                                            {selectedShippingOption?.isPickup ? (
                                                <div className="col-span-1 md:col-span-2 mt-2">
                                                    <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-700 flex items-start">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <span>For pickup orders, the address has been automatically filled with the pickup location details.</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="md:col-span-2">
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Make sure to enter a complete shipping address for delivery orders.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Transaction Details */}
                        <div className="bg-white rounded-md">
                            <button
                                type="button"
                                onClick={() => setShowTransactionDetails(!showTransactionDetails)}
                                className="flex w-full items-center text-[#191970] hover:text-opacity-80 py-2"
                            >
                                {showTransactionDetails ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                <CreditCard size={18} className="ml-1 mr-2" />
                                <span className="font-medium">Transaction Details</span>
                            </button>

                            {showTransactionDetails && (
                                <div className="mt-2 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Status
                                            </label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                                className={`${dropdownStyles} ${dropdownArrowStyles}`}
                                            >
                                                <option value="SUCCESS">Completed</option>
                                                <option value="PENDING">Pending</option>
                                                <option value="CANCELLED">Cancelled</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Payment Method
                                            </label>
                                            <select
                                                name="paymentMethod"
                                                value={formData.paymentMethod}
                                                onChange={handleInputChange}
                                                className={`${dropdownStyles} ${dropdownArrowStyles}`}
                                            >
                                                <option value="CASH">Cash</option>
                                                <option value="BANK_TRANSFER">Bank Transfer</option>
                                                <option value="POS">POS</option>
                                                <option value="WHATSAPP">WhatsApp</option>
                                                <option value="OTHER">Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">
                                                Transaction Date
                                            </label>
                                            <input
                                                type="date"
                                                name="transactionDate"
                                                value={formData.transactionDate}
                                                onChange={handleInputChange}
                                                className={`${dropdownStyles}`}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500 mb-1">
                                            <FileText size={14} className="inline mr-1" />
                                            Notes <span className="text-[70%]">(optional)</span>
                                        </label>
                                        <textarea
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            rows="2"
                                            className="w-full px-2 py-2 block rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50"
                                            placeholder="Any additional information about this transaction..."
                                        ></textarea>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-[#191970] mb-3 flex items-center">Order Summary</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="font-medium">₦{subtotal.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Shipping</span>
                                    <span className="font-medium">₦{shippingCost.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-lg font-bold pt-3 border-t border-blue-200">
                                    <span>Total</span>
                                    <span>₦{total.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-6 py-2 bg-[#191970] rounded-md text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#191970]"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Create Transaction'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ManualTransactionForm;
